import * as React from 'react';
import * as styles from './toc.module.scss';
import { usePageData } from '../../../hooks/page-data';
import unescapeAttribute from '../../../utilities/unescape-attribute';
import { useSiteMetadata } from '../../../queries/site-medatata';
import NewWindow from '../../icons/new-window';

/* eslint-disable react/jsx-no-target-blank */

const getScrollPadding = () => {
    const paddingPx = window.getComputedStyle(document.documentElement)['scrollPaddingTop'];
    if (!paddingPx) return 0;
    return parseInt(paddingPx.replace('px', ''), 10);
};

const scrollParentTo = (element) => {
    if (!element || !element.parentNode || element.parentNode.scrollHeight <= element.parentNode.offsetHeight) {
        return;
    }

    const pos = element.offsetTop - element.parentNode.offsetHeight / 2;

    element.parentNode.scrollTo({ top: pos, behavior: 'smooth' });
}

const SfToc = () => {
    const pageData = usePageData();
    const [activeId, setActiveId] = React.useState(null);
    const timerRef = React.useRef(null);
    const siteMetadata = useSiteMetadata();
    const itemsWrapRef = React.useRef(null);

    const items = React.useMemo(() => {
        const hAst = pageData?.markdownRemark?.htmlAst;
        if (!hAst) {
            return [];
        }

        const result = hAst.children
            .filter(node => node.tagName === 'h2' && node.properties?.dataToc)
            .map(n => ({
                id: n.properties.id,
                text: unescapeAttribute(n.properties.dataToc),
            }));

        return result;
    }, [pageData]);

    React.useEffect(() => {
        if (items.length === 0) {
            return;
        }

        const handler = () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            let newId = null;
            const scrollY = Math.ceil(window.scrollY + getScrollPadding());

            for (const item of items) {
                const el = document.getElementById(item.id);
                if (!el) {
                    continue;
                }
                if (el.offsetTop > scrollY + 1) {
                    break;
                }
                newId = el.id;
            }

            timerRef.current = setTimeout(() => setActiveId(newId), 100);
        };

        window.addEventListener('scroll', handler, { passive: true });
        handler();

        return () => {
            window.removeEventListener('scroll', handler);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [items]);

    const ctaLinks = React.useMemo(() => {
        const result = siteMetadata.sitefinityCtaLinks.filter(link => link.url && link.text);
        return result;
    }, [siteMetadata.sitefinityCtaLinks]);

    React.useEffect(() => {
        if (!activeId || !itemsWrapRef.current) {
            return;
        }

        const activeLink = itemsWrapRef.current.querySelector(`a[href="#${activeId}"]`);
        if (!activeLink) {
            return;
        }

        if (itemsWrapRef.current.scrollHeight <= itemsWrapRef.current.offsetHeight) {
            return;
        }

        scrollParentTo(activeLink);

    }, [activeId]);

    return <>
        <div className={styles.container}>
            {
                items.length > 0 && <>
                    <div className={styles.title}>This Article Contains</div>
                    <div ref={itemsWrapRef} className={styles.items}>
                        {
                            items.map(item => <a key={item.id} href={`#${item.id}`} className={item.id === activeId ? styles.active : ''}>{item.text}</a>)
                        }
                    </div>
                </>
            }
            {
                items.length > 0 && ctaLinks.length > 0 && <>
                    <div className={styles.divider}></div>
                </>
            }
            {
                ctaLinks.length > 0 && <>
                    <div className={styles.title}>New to Sitefinity?</div>
                    <div className={styles.ctaLinks}>
                        {
                            ctaLinks.map(link => <a key={link.url} href={link.url} className={styles.ctaLink} target='_blank' rel="noopener">{link.text}<NewWindow></NewWindow></a>)
                        }
                    </div>
                </>
            }
        </div>
    </>
}

export default SfToc;